import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import * as api from '../../services/teacherApi';
import { attemptRow, DataFigure } from '../../components/teacherAnalytics/AnalyticsFigures';
import { StudentCards } from '../../components/teacherAnalytics/StudentCards';
import {
  AnalyticsGate, CohortView, CompletionView, CourseShell, LoadState, MeasureView, Pager, Panel,
  Placements, presenceCopy, Records, RestoreFocus, Search, useAnalytics, useQueryState
} from '../../components/teacherAnalytics/AnalyticsShared';

function Topics({courseId}: {courseId: string}) {
  const {pageNumber, update} = useQueryState();
  const page = pageNumber();
  const resource = useAnalytics(`${courseId}/topics/${page}`, signal => api.getAnalyticsTopics(courseId, {page, limit: 20}, signal));
  return <Panel><h2 data-results-heading tabIndex={-1}>知识元概览</h2>
    <LoadState resource={resource}>{(data, meta) => <>
      <RestoreFocus ready />
      {!data.topics.items.length && <p>{page > 1 ? '当前页暂无记录。' : '此课程暂无知识元。'}</p>}
      <DataFigure title="知识元已记录完成率对比（本页）" scale="percent"
        caption={`第 ${data.topics.pagination.page} 页知识元 · 全部在籍学生`}
        rows={data.topics.items.map(t => ({
          id: t.topicId, label: t.title || `知识元：${t.topicId}`,
          value: t.overview.completionRate, availability: t.overview.completedStudents.availability,
          detail: `${t.placements.map(p => [p.chapterTitle || '未命名章节', p.sectionTitle].filter(Boolean).join(' / ')).join('；')} · ${t.overview.completedStudents.value ?? '—'}/${t.overview.enrolledStudents} 人 · 有可用统计 ${t.overview.completedStudents.coverage.withEvidence}/${t.overview.completedStudents.coverage.population} 人 · 无可用统计 ${t.overview.completedStudents.coverage.withoutEvidence} 人`
        }))} />
      <Records>{data.topics.items.map((t, i, items) => <React.Fragment key={t.topicId}>
        {(i === 0 || t.placements[0]?.chapterId !== items[i - 1].placements[0]?.chapterId ||
          t.placements[0]?.sectionId !== items[i - 1].placements[0]?.sectionId) &&
          <h3>{t.placements[0]?.chapterTitle || '未命名章节'}{t.placements[0]?.sectionTitle && ` / ${t.placements[0].sectionTitle}`}</h3>}
        <article><h3>{t.title || `知识元信息不可用 · ${t.topicId}`}</h3><Placements topic={t} />
          <CohortView value={t.overview} generatedAt={meta.generatedAt} showFigure={false} />
          <Link to={`?tab=students&topicId=${t.topicId}&page=1`} state={{topicPage: page}}>查看学生表现</Link>
        </article>
      </React.Fragment>)}</Records>
      <Pager pagination={data.topics.pagination} onPage={p => update({page: p})} />
    </>}</LoadState>
  </Panel>;
}
function TopicStudents({courseId, topicId}: {courseId: string; topicId: string}) {
  const {params, update, pageNumber} = useQueryState();
  const page = pageNumber();
  const q = params.get('q') || '';
  const location = useLocation();
  const previousPage = Number((location.state as {topicPage?: number} | null)?.topicPage) || 1;
  const resource = useAnalytics(`${courseId}/topic/${topicId}/${page}/${q}`,
    signal => api.getTopicAnalyticsStudents(courseId, topicId, {page, limit: 20, q}, signal));
  return <Panel><h2 data-results-heading tabIndex={-1}>知识元学生表现</h2>
    <Link to={`?tab=topics&page=${previousPage}`}>返回知识元概览</Link>
    <Search value={q} loading={resource.loading} onSearch={value => update({q: value || null, page: 1}, 'analytics-name-search')} />
    <LoadState resource={resource}>{(data, meta) => <>
      <RestoreFocus ready />
      <h3>{data.topic.title || `知识元信息不可用 · ${data.topic.topicId}`}</h3><Placements topic={data.topic} />
      <CohortView value={data.overview} generatedAt={meta.generatedAt} />
      <details><summary>本页学生练习次数对比</summary>
      <DataFigure title="学生已记录练习次数对比（本页）" scale="count"
        caption={`第 ${data.students.pagination.page} 页 · ${q ? '搜索结果中的学生' : '学生列表'}`}
        rows={data.students.items.map(s => attemptRow(s.studentId, `${s.name || '未命名学生'} · ${s.studentId}`, s.attempts))} />
      </details>
      {!data.students.items.length && <p>{page > 1 ? '当前页暂无记录。' : q ? '未找到匹配的学生。' : '暂无学生注册此课程。'}</p>}
      <StudentCards students={data.students.items} courseId={courseId} topicId={topicId}
        label="知识元全部在籍学生表现"
        summaryMetrics={s => [{label: '本知识元', value: <CompletionView value={s.completion} compact />}]} metrics={s => [
          {label: '完成状态', value: <CompletionView value={s.completion} />},
          {label: '已记录练习次数', value: <MeasureView measure={s.attempts} unit=" 次" />},
          {label: '练习用时', value: <MeasureView measure={s.exerciseTimeSeconds} unit=" 秒" />},
          {label: '自我解释（本课程）', value: presenceCopy(s.courseExplanationPresent)}
        ]} />
      <Pager pagination={data.students.pagination} onPage={p => update({page: p})} />
    </>}</LoadState>
  </Panel>;
}
function Analytics() {
  const {courseId = ''} = useParams();
  const {params} = useQueryState();
  const tab = params.get('tab') || 'overview';
  const topicId = params.get('topicId') || '';
  const resource = useAnalytics(`${courseId}/overview`, signal => api.getCourseAnalytics(courseId, signal), tab === 'overview');
  // The v1 roster carries CourseCard. A one-row read avoids a full-cohort scan
  // merely to label topic pages; discard the row before storing shell state.
  const shell = useAnalytics(`${courseId}/shell`, async signal => {
    const response = await api.getAnalyticsStudents(courseId, {page: 1, limit: 1}, signal);
    return {...response, data: {course: response.data.course}};
  }, tab === 'topics' || tab === 'students');
  return <CourseShell course={resource.result?.data.course || shell.result?.data.course}>
    {tab === 'overview' && <LoadState resource={resource} label="正在加载课程…">{(data, meta) =>
      <Panel><h2 data-results-heading tabIndex={-1}>课程概览 · 已记录完成课程</h2>
        {!data.course.assignedTopics && <p>此课程暂无知识元。</p>}
        <CohortView value={data.overview} generatedAt={meta.generatedAt} />
      </Panel>
    }</LoadState>}
    {tab !== 'overview' && <LoadState resource={shell} label="正在加载课程…">{() => null}</LoadState>}
    {tab === 'topics' && <Topics courseId={courseId} />}
    {tab === 'students' && topicId && <TopicStudents courseId={courseId} topicId={topicId} />}
    {(!['overview', 'topics', 'students'].includes(tab) || (tab === 'students' && !topicId)) &&
      <Panel role="alert">链接或查询条件无效，请返回列表重试。</Panel>}
  </CourseShell>;
}
export const CourseAnalyticsPage = () => <AnalyticsGate><Analytics /></AnalyticsGate>;
